import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import type { Chart } from "chart.js";
import RefreshIcon from "@mui/icons-material/Refresh";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CropFreeIcon from "@mui/icons-material/CropFree";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ImageIcon from "@mui/icons-material/Image";
import { useState } from "react";
import { CHART_BACKGROUND_COLOR } from "../DVHChart";

interface Props {
  chartRef: React.RefObject<Chart<"line", number[], number> | undefined>;
}

const ChartControlsOptions = {
  ZOOM: "zoom",
  PAN: "pan",
};

const DOWNLOADED_FILE_NAME = "dvh_chart.png";

export const ChartControls = ({ chartRef }: Props) => {
  const [currentControl, setCurrentControl] = useState<string>(
    ChartControlsOptions.ZOOM,
  );

  const handleResetZoom = () => {
    if (!chartRef || !chartRef.current) {
      return;
    }

    chartRef.current.resetZoom();
  };

  const handleControlsChange = (
    _: React.MouseEvent<HTMLElement>,
    value: string,
  ) => {
    if (!chartRef || !chartRef.current) return;

    if (!value) return;

    const zoomPlugin = chartRef.current.options.plugins?.zoom;

    if (!zoomPlugin?.pan || !zoomPlugin?.zoom?.drag) {
      return;
    }

    zoomPlugin.pan.enabled = value === ChartControlsOptions.PAN;
    zoomPlugin.zoom.drag.enabled = value === ChartControlsOptions.ZOOM;
    setCurrentControl(value);
    chartRef.current.update();
  };

  const handleZoomInOut = (value: number) => {
    if (!chartRef || !chartRef.current) {
      return;
    }
    chartRef.current.zoom(value);
  };

  const handleImage = () => {
    if (!chartRef || !chartRef.current) {
      return;
    }

    const image = chartRef.current.toBase64Image();
    if (!image) return;

    const link = document.createElement("a");
    link.href = image;
    link.download = DOWNLOADED_FILE_NAME;
    link.click();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        paddingY: 1,
        backgroundColor: CHART_BACKGROUND_COLOR,
        flex: 1,
      }}
    >
      <ToggleButtonGroup
        sx={{
          backgroundColor: "rgba(153, 153, 153, 0.1)",
          mr: "24px",
          mt: "10px",
          "& .MuiToggleButton-root.Mui-selected": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            color: "white",

            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
            },
          },

          "& .MuiToggleButton-root:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          },
        }}
        onChange={handleControlsChange}
        exclusive
        value={currentControl}
      >
        <Tooltip title="Drag to zoom in" arrow placement="top">
          <ToggleButton value={ChartControlsOptions.ZOOM}>
            <CropFreeIcon sx={{ color: "white" }} />
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Drag to pan" arrow placement="top">
          <ToggleButton value={ChartControlsOptions.PAN}>
            <OpenWithIcon sx={{ color: "white" }} />
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Zoom in" arrow placement="top">
          <ToggleButton
            value={""}
            onClick={() => handleZoomInOut(1.1)}
            sx={{ color: "white" }}
          >
            <ZoomInIcon sx={{ color: "white" }} />
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Zoom out" arrow placement="top">
          <ToggleButton
            value={""}
            onClick={() => handleZoomInOut(0.9)}
            sx={{ color: "white" }}
          >
            <ZoomOutIcon sx={{ color: "white" }} />
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Reset view" arrow placement="top">
          <ToggleButton
            onClick={handleResetZoom}
            value={""}
            sx={{ color: "white" }}
          >
            <RefreshIcon sx={{ color: "white" }} />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Download chart as image" placement="top" arrow>
          <ToggleButton
            onClick={handleImage}
            value={""}
            sx={{ color: "white" }}
          >
            <ImageIcon sx={{ color: "white" }} />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ChartControls;
